import { AwsRum, AwsRumConfig } from 'aws-rum-web';

interface RumConfig {
  appMonitorId: string;
  identityPoolId: string;
  region: string;
  applicationVersion?: string;
}

let awsRumClient: AwsRum | null = null;

/**
 * Initialize AWS RUM monitoring for the application
 * Call this once at application startup
 */
export const initializeRUM = (config: RumConfig): AwsRum | null => {
  try {
    // Don't initialize multiple times
    if (awsRumClient) {
      console.warn('AWS RUM already initialized');
      return awsRumClient;
    }

    // Validate required configuration
    if (!config.appMonitorId || !config.identityPoolId || !config.region) {
      console.warn('AWS RUM configuration incomplete, skipping initialization');
      return null;
    }

    const rumConfig: AwsRumConfig = {
      sessionSampleRate: import.meta.env.VITE_NODE_ENV === 'prod' ? 1.0 : 1.0, // Set the sample rate to 100% while we still have very few users
      identityPoolId: config.identityPoolId,
      endpoint: `https://dataplane.rum.${config.region}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false, // Disable X-Ray tracing, can be turned on later
    };

    awsRumClient = new AwsRum(
      config.appMonitorId,
      config.applicationVersion || '1.0.0',
      config.region,
      rumConfig
    );

    return awsRumClient;
  } catch (error) {
    console.error('Failed to initialize AWS RUM:', error);
    return null;
  }
};

/**
 * Get the initialized RUM client
 * Returns null if RUM hasn't been initialized
 */
export const getRumClient = (): AwsRum | null => awsRumClient;

/**
 * Record a custom event with RUM
 */
export const recordEvent = (name: string, details?: Record<string, any>): void => {
  if (awsRumClient) {
    try {
      awsRumClient.recordEvent(name, details || {});
    } catch (err) {
      console.warn('Failed to record RUM event:', err);
    }
  }
};

/**
 * Record an error with RUM
 */
export const recordError = (error: Error, details?: Record<string, any>): void => {
  if (awsRumClient) {
    try {
      awsRumClient.recordError(error);
      // If details are provided, record them as a separate event
      if (details && Object.keys(details).length > 0) {
        awsRumClient.recordEvent('error_details', details);
      }
    } catch (err) {
      console.warn('Failed to record RUM error:', err);
    }
  }
};

/**
 * Start a timing event (for measuring custom performance metrics)
 * Uses recordEvent to track timing starts
 */
export const startTiming = (name: string): void => {
  if (awsRumClient) {
    try {
      awsRumClient.recordEvent(`${name}_start`, { timestamp: Date.now() });
    } catch (err) {
      console.warn('Failed to start RUM timing:', err);
    }
  }
};

/**
 * Stop a timing event
 * Uses recordEvent to track timing ends
 */
export const stopTiming = (name: string, details?: Record<string, any>): void => {
  if (awsRumClient) {
    try {
      awsRumClient.recordEvent(`${name}_end`, {
        timestamp: Date.now(),
        ...details,
      });
    } catch (err) {
      console.warn('Failed to stop RUM timing:', err);
    }
  }
};

export default {
  initializeRUM,
  getRumClient,
  recordEvent,
  recordError,
  startTiming,
  stopTiming,
};
